import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * "Jump to section" tabs — Figma 3032:1253 (Screen / review prescription
 * 3031:9447). A wrapping strip of pills above the builder cards: click one to
 * scroll its card into view, and the active pill follows whichever card is
 * currently in view.
 *
 * Geometry read from Dev Mode: 29px pills, fully rounded, 13px side padding,
 * 8px gap on BOTH axes (the strip wraps to a second row), 1px border —
 * $control-border when idle, $purple when active (which also colours the text).
 */
const RxSectionTabs = ({ tabs, activeId, onJump }) => (
  <nav className="rx-tabs" aria-label="Jump to section">
    {tabs.map((t) => (
      <button
        key={t.id}
        type="button"
        className={`rx-tab${activeId === t.id ? " is-active" : ""}`}
        aria-current={activeId === t.id ? "true" : undefined}
        onClick={() => onJump(t.id)}
      >
        {t.label}
      </button>
    ))}
  </nav>
);
RxSectionTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, label: PropTypes.string.isRequired })
  ).isRequired,
  activeId: PropTypes.string,
  onJump: PropTypes.func.isRequired,
};

/**
 * How far down the sticky pane head reaches — MEASURED, not assumed.
 *
 * `.rx-pane-head` is `position: sticky; top: 0` and holds the caption plus the
 * chip strip, and that strip WRAPS: its height is 132px at 1600 wide and more
 * when the pills need a third row. Two things depended on guessing it:
 *
 *  - the jump. `scrollIntoView({ block: "start" })` puts a section's top at the
 *    scrollport's top, which is BEHIND the head — measured at 132px of the
 *    Patient-details card hidden, with its first pixels failing
 *    elementFromPoint. The offset is published as a CSS custom property and
 *    spent as `scroll-margin-top` (prescription.scss), which scrollIntoView
 *    subtracts for whichever element is actually scrolling — the pane above
 *    1181px, the window below it.
 *  - the highlight. The trigger line was a hardcoded 96, so a section became
 *    "current" while its top was still 36px underneath the head.
 *
 * One measurement drives both, so they cannot drift apart.
 */
/**
 * WHICH element is scrolling the builder.
 *
 * Above 1181px `.rx-host.is-pinned > .rx-pane` is `overflow-y: auto`, so the
 * pane scrolls and the window does not; below that the page scrolls. This used
 * to read `document.scrollingElement` unconditionally, and the consequence was
 * not a stale highlight but a WRONG one: in the pinned layout the window sits
 * at its own scroll bottom, so the "at the bottom, the last section wins" rule
 * fired on every window scroll or resize and snapped the strip to "Follow-up"
 * no matter which card was on screen.
 */
const scrollerOf = () => {
  const pane = document.querySelector(".rx-host.is-pinned > .rx-pane");
  if (pane && pane.scrollHeight > pane.clientHeight + 1) return pane;
  return document.scrollingElement || document.documentElement;
};

const JUMP_GAP = 4; // a hair of clearance, against sub-pixel rounding
const headOffset = () => {
  const head = document.querySelector(".rx-host .rx-pane-head");
  if (!head) return 96; // pre-mount fallback: the old hardcoded guess
  return Math.ceil(head.getBoundingClientRect().height) + JUMP_GAP;
};

/**
 * Tracks which section is in view so the strip highlights it.
 *
 * Reads LIVE geometry on scroll rather than caching IntersectionObserver
 * entries: cached entries hold the position from when they fired, so sorting by
 * them picks the wrong section once you've scrolled past.
 *
 * Two rules that matter:
 *  - at the bottom of the page the LAST section wins, because the final cards
 *    can never reach the trigger line — without this, clicking "Follow-up"
 *    highlights whichever card happens to sit at the top instead.
 *  - a click wins outright for a moment, so the smooth scroll doesn't fight
 *    the user's intent on the way there.
 */
export const useSectionSpy = (ids, deps) => {
  const [activeId, setActiveId] = useState(ids[0]);
  const lockUntil = useRef(0);

  useEffect(() => {
    let queued = false;

    const measure = () => {
      queued = false;
      /* Published on every measure — which covers load, scroll and resize, so a
         chip strip that rewraps updates the jump offset with it. */
      const offset = headOffset();
      document.documentElement.style.setProperty("--rx-jump-offset", `${offset}px`);
      if (Date.now() < lockUntil.current) return;
      const scroller = scrollerOf();
      const atBottom =
        scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4;
      if (atBottom) {
        const last = ids.filter((id) => document.getElementById(id)).pop();
        if (last) setActiveId(last);
        return;
      }
      let current = null;
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        // The last section whose top has passed the trigger line is the one
        // being read; before any has, the first stays active.
        if (el.getBoundingClientRect().top - offset <= 0) current = id;
      });
      setActiveId(current || ids.find((id) => document.getElementById(id)) || ids[0]);
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(measure);
    };

    measure();
    /* CAPTURE on document, not a listener on window. A scroll event does not
       bubble, so a window listener never hears the PANE scrolling — and above
       1181px the pane is the scroller. Capture at the document sees both. */
    document.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    setActiveId(id);
    // Hold the clicked tab while the smooth scroll runs.
    lockUntil.current = Date.now() + 900;
    /* Re-measured HERE rather than trusted from the last scroll: the first jump
       can happen before any scroll event has fired, and the strip may have
       rewrapped since. */
    document.documentElement.style.setProperty("--rx-jump-offset", `${headOffset()}px`);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return { activeId, jumpTo };
};

export default RxSectionTabs;
